/**
 * Reusable form component for creating and editing flows
 */
import React, { useState } from "react";
import { Save, X } from "lucide-react";
import { Script, ScriptCreate, ScriptUpdate } from "../../types/script";
import { Textarea } from "../ui/Input";

interface FlowFormProps {
  flow?: Script;
  onSubmit: (data: ScriptCreate | ScriptUpdate) => void;
  onCancel: () => void;
  isLoading?: boolean;
  submitText?: string;
  hideReplayNodeGroups?: boolean;
  campaignOptions?: { id: number; name: string }[];
}

interface FormData {
  name: string;
  description: string;
  campaign_id: string;
  conversation_template_id: string;
  replay_node_groups: string;
}

interface FormErrors {
  name?: string;
  description?: string;
  campaign_id?: string;
  conversation_template_id?: string;
  replay_node_groups?: string;
}

const FlowForm: React.FC<FlowFormProps> = ({
  flow,
  onSubmit,
  onCancel,
  isLoading = false,
  submitText = "Save Flow",
  hideReplayNodeGroups = false,
  campaignOptions,
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: flow?.name || "",
    description: flow?.description || "",
    campaign_id: flow?.campaign_id?.toString() || "",
    conversation_template_id: flow?.conversation_template_id || "",
    replay_node_groups: flow?.replay_node_groups?.join(", ") || "",
  });

  const [errors, setErrors] = useState<FormErrors>({});

  React.useEffect(() => {
    setFormData({
      name: flow?.name || "",
      description: flow?.description || "",
      campaign_id: flow?.campaign_id?.toString() || "",
      conversation_template_id: flow?.conversation_template_id || "",
      replay_node_groups: flow?.replay_node_groups?.join(", ") || "",
    });
  }, [flow]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Flow name is required";
    } else if (formData.name.length < 3) {
      newErrors.name = "Name must be at least 3 characters";
    } else if (formData.name.length > 100) {
      newErrors.name = "Name must be less than 100 characters";
    }

    // Campaign ID validation
    if (!formData.campaign_id.trim()) {
      newErrors.campaign_id = "Campaign ID is required";
    } else if (
      isNaN(Number(formData.campaign_id)) ||
      Number(formData.campaign_id) <= 0
    ) {
      newErrors.campaign_id = "Campaign ID must be a positive number";
    }

    // Template ID validation
    if (!formData.conversation_template_id.trim()) {
      newErrors.conversation_template_id = "Template ID is required";
    } else if (formData.conversation_template_id.length < 3) {
      newErrors.conversation_template_id =
        "Template ID must be at least 3 characters";
    } else if (formData.conversation_template_id.length > 255) {
      newErrors.conversation_template_id =
        "Template ID must be less than 255 characters";
    }

    // Description validation
    if (formData.description.length > 500) {
      newErrors.description = "Description must be less than 500 characters";
    }

    // Replay node groups validation
    if (!hideReplayNodeGroups && formData.replay_node_groups.trim()) {
      const groups = formData.replay_node_groups
        .split(",")
        .map((g) => g.trim());
      if (!groups.every((g) => !isNaN(Number(g)) && Number(g) > 0)) {
        newErrors.replay_node_groups =
          "Must be comma-separated positive numbers";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Transform form data
    const submitData: any = {
      name: formData.name.trim(),
      campaign_id: Number(formData.campaign_id),
      conversation_template_id: formData.conversation_template_id.trim(),
    };

    // Add description only if it's not empty
    if (formData.description.trim()) {
      submitData.description = formData.description.trim();
    }

    // Include replay node groups only when not hidden
    if (!hideReplayNodeGroups) {
      submitData.replay_node_groups = formData.replay_node_groups.trim()
        ? formData.replay_node_groups
            .split(",")
            .map((n) => Number(n.trim()))
            .filter((n) => !isNaN(n))
        : [];
    }

    onSubmit(submitData);
  };

  const isFormValid = () => {
    return (
      formData.name.trim() &&
      formData.campaign_id.trim() &&
      !isNaN(Number(formData.campaign_id)) &&
      Number(formData.campaign_id) > 0 &&
      formData.conversation_template_id.trim() &&
      formData.description.length <= 500
    );
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="border-b border-gray-200 px-6 py-4">
        <h2 className="text-lg font-medium text-gray-900">
          {flow ? "Edit Flow" : "Create New Flow"}
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          {flow
            ? "Update the flow information below."
            : "Enter the details for your new conversation flow."}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="px-6 py-4">
        <div className="space-y-6">
          {/* Flow Name */}
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Flow Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Enter flow name"
              className={`block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                errors.name
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              }`}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <Textarea
              label="Description"
              id="description"
              rows={4}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Enter flow description (optional)"
              variant="rounded-md"
              className={
                errors.description
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                  : ""
              }
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              {formData.description.length}/500 characters
            </p>
          </div>

          {/* Campaign ID */}
          <div>
            <label
              htmlFor="campaign_id"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Campaign {campaignOptions ? "Select" : "ID"} *
            </label>
            {campaignOptions ? (
              <select
                id="campaign_id"
                value={formData.campaign_id}
                onChange={(e) =>
                  handleInputChange("campaign_id", e.target.value)
                }
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                  errors.campaign_id
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                }`}
              >
                <option value="">Select a campaign</option>
                {campaignOptions.map((opt) => (
                  <option key={opt.id} value={opt.id.toString()}>
                    {opt.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="number"
                id="campaign_id"
                min="1"
                value={formData.campaign_id}
                onChange={(e) =>
                  handleInputChange("campaign_id", e.target.value)
                }
                placeholder="Enter campaign ID"
                className={`block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                  errors.campaign_id
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                }`}
              />
            )}
            {errors.campaign_id && (
              <p className="mt-1 text-sm text-red-600">{errors.campaign_id}</p>
            )}
          </div>

          {/* Template ID */}
          <div>
            <label
              htmlFor="conversation_template_id"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Template ID *
            </label>
            <input
              type="text"
              id="conversation_template_id"
              value={formData.conversation_template_id}
              onChange={(e) =>
                handleInputChange("conversation_template_id", e.target.value)
              }
              placeholder="Enter template ID"
              className={`block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                errors.conversation_template_id
                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              }`}
            />
            {errors.conversation_template_id && (
              <p className="mt-1 text-sm text-red-600">
                {errors.conversation_template_id}
              </p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Unique identifier for this flow template within the campaign
            </p>
          </div>

          {/* Replay Global Flows */}
          {!hideReplayNodeGroups && (
            <div>
              <label
                htmlFor="replay_node_groups"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Replay Global Flows
              </label>
              <input
                type="text"
                id="replay_node_groups"
                value={formData.replay_node_groups}
                onChange={(e) =>
                  handleInputChange("replay_node_groups", e.target.value)
                }
                placeholder="Enter global flow IDs separated by commas (e.g., 1, 2, 3)"
                className={`block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                  errors.replay_node_groups
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                }`}
              />
              {errors.replay_node_groups && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.replay_node_groups}
                </p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Optional: Specify which global flows this flow can replay from
              </p>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 font-medium rounded-lg bg-gray-200 text-gray-900 hover:bg-gray-300 transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || !isFormValid()}
            className="inline-flex items-center px-4 py-2 font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isLoading ? "Saving..." : submitText}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FlowForm;
